// One strategy as a polished card. Replaces the table row pattern with a richer
// surface: name + status badge + asset chips + last-signal + StrategySpecSummary
// + action buttons. Click anywhere on the card body (outside the action area)
// to select it as the Monitor tab's focus.

import type { StrategyDto } from "../../api/types";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StrategyActions } from "./StrategyActions";
import { StrategySpecSummary } from "./StrategySpecSummary";
import { parseBackendTimestamp } from "../../hooks/time";
import { useSelectedStrategy } from "../../state/SelectedStrategyContext";
import { cn } from "@/lib/utils";
import { CircleDot, Zap } from "lucide-react";

interface Props {
  strategy: StrategyDto;
  onChanged: () => void;
}

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  active: { className: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400", label: "Active" },
  paused: { className: "border-amber-500/40 bg-amber-500/10 text-amber-400", label: "Paused" },
  draft: { className: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400", label: "Draft" },
};

export function StrategyCard({ strategy, onChanged }: Props) {
  const { selectedStrategyId, setSelectedStrategyId } = useSelectedStrategy();
  const isSelected = selectedStrategyId === strategy.id;
  const status = STATUS_BADGE[strategy.status] ?? STATUS_BADGE.draft!;
  const lastSignal = strategy.lastSignalBarTime
    ? formatRelative(parseBackendTimestamp(strategy.lastSignalBarTime))
    : "—";
  const created = formatRelative(parseBackendTimestamp(strategy.createdAt));

  return (
    <Card
      onClick={() => setSelectedStrategyId(strategy.id)}
      className={cn(
        "group p-4 cursor-pointer transition-all bg-card hover:bg-card-elevated hover:border-primary/40",
        isSelected && "ring-2 ring-primary/60 border-primary/60",
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Body */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-base truncate">{strategy.name}</h3>
            <Badge variant="outline" className={cn("border", status.className)}>
              <CircleDot className="size-3 mr-1" />
              {status.label}
            </Badge>
            {isSelected && (
              <Badge variant="secondary" className="text-[10px]">
                <Zap className="size-2.5 mr-1" />
                Monitor focus
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-wrap text-xs">
            <Badge variant="outline" className="font-mono">{strategy.spec.coin}</Badge>
            <Badge variant="outline" className="font-mono">{strategy.spec.interval}</Badge>
            <Badge variant="outline" className="font-mono uppercase">{strategy.spec.marketType}</Badge>
            <span className="text-muted-foreground">
              · {strategy.channels.length} channel{strategy.channels.length === 1 ? "" : "s"}
            </span>
            <span className="text-muted-foreground">· last signal {lastSignal}</span>
            <span className="text-muted-foreground">· created {created}</span>
          </div>

          <div className="pt-2 border-t border-border">
            <StrategySpecSummary spec={strategy.spec} compact />
          </div>
        </div>

        {/* Actions */}
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <StrategyActions strategy={strategy} onChanged={onChanged} />
        </div>
      </div>
    </Card>
  );
}

function formatRelative(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  const delta = Date.now() - ms;
  if (delta < 0) return new Date(ms).toLocaleString();
  if (delta < 60_000) return "just now";
  if (delta < 3600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86400_000) return `${Math.floor(delta / 3600_000)}h ago`;
  return `${Math.floor(delta / 86400_000)}d ago`;
}
